// FILE: AgentsClient.ts
// Purpose: HTTP wrapper around `/api/admin/agents*` and the non-admin `/api/agents`
//          read, mirroring TeamsClient's shape (plain object, static methods) over
//          the shared `adminFetch` helper.
// Layer: Web client
// Exports: `AgentsClient`

import type {
  AdminAgentCreateRequest,
  AdminAgentResponse,
  AdminAgentsResponse,
  AdminAgentUpdateRequest,
  ManagedAgent,
  ManagedAgentId,
} from "@taskin-code/contracts";

import { parseManagedAgentMode } from "@taskin-code/shared/managedAgentMode";

import { adminFetch } from "../lib/adminFetch";

/**
 * Fill in `mode` when the response does not carry it.
 *
 * These reads are typed casts, not schema decodes, so a body written by a server
 * that predates the field arrives with `mode: undefined` — and every picker
 * filters on it, so an undefined mode would empty the menu instead of behaving
 * like the agent always did. `parseManagedAgentMode` lands it on `general`,
 * which is both the column default and the pre-mode behaviour. Applied at the
 * single point every agent enters the web from, so no view has to be defensive.
 */
const withMode = (agent: ManagedAgent): ManagedAgent => ({
  ...agent,
  mode: parseManagedAgentMode(agent.mode as unknown as string),
});

export const AgentsClient = {
  /** `a.view`-gated: every agent, for the admin table. */
  async list(): Promise<ReadonlyArray<ManagedAgent>> {
    const result = await adminFetch<AdminAgentsResponse>("/api/admin/agents");
    return result.agents.map(withMode);
  },

  /** No permission gate beyond a session: the model picker's read, scoped server-side by team. */
  async listVisible(): Promise<ReadonlyArray<ManagedAgent>> {
    const result = await adminFetch<AdminAgentsResponse>("/api/agents");
    return result.agents.map(withMode);
  },

  async create(input: AdminAgentCreateRequest): Promise<ManagedAgent> {
    const result = await adminFetch<AdminAgentResponse>("/api/admin/agents", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return withMode(result.agent);
  },

  async update(agentId: ManagedAgentId, patch: AdminAgentUpdateRequest): Promise<ManagedAgent> {
    const result = await adminFetch<AdminAgentResponse>(`/api/admin/agents/${agentId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    return withMode(result.agent);
  },

  async remove(agentId: ManagedAgentId): Promise<void> {
    await adminFetch<void>(`/api/admin/agents/${agentId}`, { method: "DELETE" });
  },

  async uploadAvatar(agentId: ManagedAgentId, dataUrl: string): Promise<string | null> {
    const result = await adminFetch<{ avatarFileName: string | null }>(
      `/api/admin/agents/${agentId}/avatar`,
      { method: "PUT", body: JSON.stringify({ dataUrl }) },
    );
    return result.avatarFileName;
  },

  async deleteAvatar(agentId: ManagedAgentId): Promise<string | null> {
    const result = await adminFetch<{ avatarFileName: string | null }>(
      `/api/admin/agents/${agentId}/avatar`,
      { method: "DELETE" },
    );
    return result.avatarFileName;
  },
};
